import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/context/AuthProvider";
import { useSecurity } from "../../auth/registered/SEC_03_03_SecurityContext";
import { useNavigationContext } from "../common/navigation/NavigationContext";
import { useCompanyContext } from "../common/company/CompanyContext";
import { useCorporateDetailsController } from "./CorporateDetailsController";
import { useCorporateForm } from "./useCorporateForm"; 
import { GoldenSourceHelper } from "../../app/Switch_Core_React_Standards";
import ManageStructure from "./ManageStructure";
// 🎯 Alias bypasses the underscore naming rules for react/jsx-pascal-case
import CompanyUsers from "./users/SEC_CompanyUsersController";
import SupplierController from "./suppliers/SupplierController";
import CompanyTaxonomyPanel from "./taxonomy/CompanyTaxonomyPanel";

/**
 * 🎯 CORPORATE DETAILS VIEW
 * MVC View providing Registry Management and Hierarchy Linking.
 */
const VALID_TABS = ["DETAILS", "STRUCTURE", "USERS", "SUPPLIERS", "TAXONOMY"];

export default function CorporateDetailsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInitialising } = useAuth();
  const { isGlobalAdmin, myCorporateEntityIds } = useSecurity();
  const { activeWorkflow, setWorkflowContext } = useNavigationContext();
  const { activeCompanyId, activeCompanyName, scopedEntityIds } = useCompanyContext();

  // 🛰️ API Controller Integration
  const {
    saveEntity,
    getRegistry,
    getTree,
    loading: apiLoading,
  } = useCorporateDetailsController();

  // 🔘 Local UI State
  // 🎯 Deep-linkable from the Company Dashboard's action buttons (?tab=STRUCTURE etc.)
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(requestedTab) ? requestedTab : "DETAILS"
  );
  const [companyList, setCompanyList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showStructureManager, setShowStructureManager] = useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);

  const DEFAULT_LABEL = process.env.REACT_APP_SITE_DEFAULTLABEL || "EFRAG";
  const VALID_COUNTRIES = ["UK", "FR", "CH", "DE"];

  /**
   * 🎯 DATA LOAD LOGIC
   */
  const loadRegistry = useCallback(async () => {
    const data = await getRegistry();
    if (!data) return;

    // 🎯 TENANT LOCK: a non-admin only manages the company/companies they hold
    // an active REPRESENTATIVE matrix row for (ResourceId lock). Global/Site
    // Admins still see the full registry.
    // 🎯 ACTIVE-COMPANY SCOPE: once a company has been picked from the
    // dashboard, default to just that company + its hierarchy siblings/peers
    // instead of every company the user reps.
    if (isGlobalAdmin) {
      setCompanyList(data);
    } else {
      const allowedIds = activeCompanyId && scopedEntityIds.length > 0
        ? scopedEntityIds
        : myCorporateEntityIds;
      const scoped = data.filter((c) => {
        const id = GoldenSourceHelper.get(c, "CorporateEntityId");
        return id !== null && allowedIds.includes(String(id));
      });
      setCompanyList(scoped);
    }
  }, [getRegistry, isGlobalAdmin, myCorporateEntityIds, activeCompanyId, scopedEntityIds]);

  /**
   * 🎯 FORM LOGIC (Decoupled)
   */
  const { formData, updateField, handleSubmit, isSubmitting } =
    useCorporateForm(saveEntity, loadRegistry);

  /**
   * 🎯 SELECTION & ACTION LOGIC
   */
  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = companyList.map((c) => 
        GoldenSourceHelper.get(c, "CorporateEntityId")
      );
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkAction = (actionType) => {
    if (selectedIds.length === 0) return alert("Please select at least one entity.");
    
    if (actionType === "LINK") {
      setShowStructureManager(true);
    } else {
      console.log(`Executing ${actionType} for IDs:`, selectedIds);
      alert(`Action "${actionType}" triggered for ${selectedIds.length} items.`);
    }
  };

  /**
   * 🎯 INITIALIZATION
   */
  useEffect(() => {
    setWorkflowContext(
      `${DEFAULT_LABEL} Corporate Hub`,
      "/registered/corporate-details"
    );
    loadRegistry();
  }, [setWorkflowContext, DEFAULT_LABEL, loadRegistry]);

  /**
   * 🎯 TAB NAVIGATION LOGIC
   */
  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    setSelectedIds([]);
    setShowStructureManager(false);
    setExpandedCompanyId(null);
    if (tabId === "STRUCTURE" && companyList.length > 0) {
      const firstId = GoldenSourceHelper.get(companyList[0], "CorporateEntityId");
      const treeData = await getTree(firstId);
      setCompanyList(treeData);
    } else if (tabId === "DETAILS") {
      loadRegistry();
    }
  };

  if (isInitialising) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-main mt-5 pt-5">
      <div className="page-main-inner container">
        <div className="row justify-content-center">
          <div className="col-xl-9">
            
            {/* 1. HEADER */}
            <div className="mb-4">
              <h2 className="h4 fw-bold mb-1">{DEFAULT_LABEL} Corporate Details</h2>
              <p className="text-muted extra-small text-uppercase fw-bold">
                Registry & User Management
              </p>
            </div>

            {/* 2. TAB SELECTOR */}
            <div className="row g-3 mb-5">
              {[
                { id: "DETAILS", label: "Company Details", icon: "bi-building" },
                { id: "STRUCTURE", label: "Structure", icon: "bi-diagram-3" },
                { id: "USERS", label: "Manage Users", icon: "bi-people" },
                { id: "SUPPLIERS", label: "Suppliers", icon: "bi-truck" },
                { id: "TAXONOMY", label: "Taxonomy", icon: "bi-diagram-2" },
              ].map((item) => (
                <div className="col" key={item.id} onClick={() => handleTabChange(item.id)}>
                  <div className={`card h-100 text-center border-0 shadow-sm p-3 ${activeTab === item.id ? "bg-dark text-white" : "bg-white"}`} style={{ cursor: "pointer" }}>
                    <i className={`bi ${item.icon} h3 mb-2`}></i>
                    <span className="extra-small fw-bold text-uppercase">{item.label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* 3. MANAGE COMPANY DETAILS (FORM) — admin-only; ordinary users create
                companies via the Register Company flow, which also assigns them
                as Representative (locked to that company) */}
            {activeTab === "DETAILS" && !showStructureManager && isGlobalAdmin && (
              <div className="card shadow-sm border-0 p-4 mb-4 position-relative">
                {(apiLoading || isSubmitting) && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white opacity-75" style={{ zIndex: 10 }}>
                    <div className="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                )}
                <h6 className="fw-bold text-uppercase extra-small text-muted mb-3 text-primary">Register Primary Entity</h6>
                <div className="row g-2">
                  <div className="col-md-4">
                    <input type="text" className="form-control form-control-sm" placeholder="Company Name *" value={formData.name} onChange={(e) => updateField("name", e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <input type="text" className="form-control form-control-sm" placeholder="Reg Number *" value={formData.regNumber} onChange={(e) => updateField("regNumber", e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <select className="form-select form-select-sm" value={formData.country} onChange={(e) => updateField("country", e.target.value)}>
                      <option value="">Select Country *</option>
                      {VALID_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn btn-sm btn-dark mt-3 fw-bold extra-small px-4" onClick={handleSubmit} disabled={apiLoading || isSubmitting}>
                  {isSubmitting ? "SAVING..." : "SAVE ENTITY"}
                </button>
              </div>
            )}

            {/* 4. LIST VIEW OR STRUCTURE MANAGER */}
            {(activeTab === "DETAILS" || activeTab === "STRUCTURE") && (
              <>
                {showStructureManager ? (
                   <ManageStructure 
                    initialSelectedIds={selectedIds} 
                    onClose={() => {
                        setShowStructureManager(false);
                        loadRegistry(); // Refresh list after managing structure
                    }} 
                   />
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex gap-2 align-items-center">
                        <span className="extra-small fw-bold text-muted text-uppercase me-2">Bulk Actions:</span>
                        
                        {/* 🎯 LINK BUTTON (Launches Sphere Manager) */}
                        {selectedIds.length > 0 && (
                          <button 
                            className="btn btn-xs btn-primary fw-bold extra-small px-3 py-1 shadow-sm"
                            onClick={() => handleBulkAction("LINK")}
                          >
                            <i className="bi bi-link-45deg me-1"></i> LINK HIERARCHY
                          </button>
                        )}

                        {["Fleet", "Location"].map((action) => (
                          <button
                            key={action}
                            className="btn btn-xs btn-outline-dark fw-bold extra-small px-3 py-1"
                            onClick={() => handleBulkAction(action)}
                            disabled={selectedIds.length === 0}
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                      {selectedIds.length > 0 && (
                        <span className="badge bg-primary extra-small">{selectedIds.length} Selected</span>
                      )}
                    </div>

                    <div className="card shadow-sm border-0 bg-white overflow-hidden">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }} className="ps-3">
                                <input 
                                  type="checkbox" 
                                  className="form-check-input"
                                  onChange={handleSelectAll}
                                  checked={companyList.length > 0 && selectedIds.length === companyList.length}
                                />
                              </th>
                              <th className="extra-small text-muted py-3">NAME</th>
                              <th className="extra-small text-muted py-3">PARENT ENTITY</th>
                              <th className="extra-small text-muted py-3">REG ID</th>
                              <th className="extra-small text-muted py-3 text-end pe-3">STATUS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyList.length > 0 ? (
                              companyList.map((c, i) => {
                                const id = GoldenSourceHelper.get(c, "CorporateEntityId");
                                const isSelected = selectedIds.includes(id);
                                const level = GoldenSourceHelper.get(c, "HierarchyLevel") || 0;
                                const parentName = GoldenSourceHelper.get(c, "ParentEntityName") || "—";

                                return (
                                  <tr key={id || i} className={isSelected ? "table-active border-primary" : ""}>
                                    <td className="ps-3">
                                      <input 
                                        type="checkbox" 
                                        className="form-check-input" 
                                        checked={isSelected} 
                                        onChange={() => toggleSelection(id)} 
                                      />
                                    </td>
                                    <td className="py-2 fw-bold small">
                                      {activeTab === "STRUCTURE" && level > 0 && (
                                        <span className="text-muted me-2">{"—".repeat(level)}</span>
                                      )}
                                      {GoldenSourceHelper.get(c, "EntityName")}
                                    </td>
                                    <td className="extra-small fw-bold text-primary">
                                        {parentName}
                                    </td>
                                    <td className="extra-small text-muted">
                                        {GoldenSourceHelper.get(c, "TaxReference") || GoldenSourceHelper.get(c, "RegistrationNumber")}
                                    </td>
                                    <td className="text-end pe-3">
                                      <span className="badge bg-light text-dark border extra-small text-uppercase">
                                        {activeTab === "STRUCTURE" ? `Lvl ${level}` : "Active"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr><td colSpan="5" className="text-center p-5 text-muted small italic">No corporate records found in registry.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* 4b. COMPANY USERS (scoped, distinct from Sustina platform Users) */}
            {activeTab === "USERS" && (
              <div className="card shadow-sm border-0 bg-white overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="extra-small text-muted py-3 ps-3">COMPANY</th>
                        <th className="extra-small text-muted py-3">REG ID</th>
                        <th className="extra-small text-muted py-3 text-end pe-3">MANAGE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyList.length > 0 ? (
                        companyList.map((c, i) => {
                          const id = GoldenSourceHelper.get(c, "CorporateEntityId");
                          const name = GoldenSourceHelper.get(c, "EntityName");
                          const isExpanded = expandedCompanyId === id;
                          return (
                            <React.Fragment key={id || i}>
                              <tr
                                onClick={() => setExpandedCompanyId(isExpanded ? null : id)}
                                style={{ cursor: "pointer" }}
                                className={isExpanded ? "table-active" : ""}
                              >
                                <td className="ps-3 fw-bold small">{name}</td>
                                <td className="extra-small text-muted">
                                  {GoldenSourceHelper.get(c, "TaxReference") || GoldenSourceHelper.get(c, "RegistrationNumber")}
                                </td>
                                <td className="text-end pe-3">
                                  <button className="btn btn-link btn-sm extra-small fw-bold p-0 text-decoration-none">
                                    {isExpanded ? "CLOSE" : "MANAGE USERS"}
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan="3" className="p-0 border-0 bg-white shadow-inner">
                                    <CompanyUsers companyId={id} companyName={name} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr><td colSpan="3" className="text-center p-5 text-muted small italic">No corporate records found in registry.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 4c. SUPPLIERS (per-company, distinct from the corporate ownership structure) */}
            {activeTab === "SUPPLIERS" && (
              <div className="card shadow-sm border-0 bg-white overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="extra-small text-muted py-3 ps-3">COMPANY</th>
                        <th className="extra-small text-muted py-3">REG ID</th>
                        <th className="extra-small text-muted py-3 text-end pe-3">MANAGE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyList.length > 0 ? (
                        companyList.map((c, i) => {
                          const id = GoldenSourceHelper.get(c, "CorporateEntityId");
                          const name = GoldenSourceHelper.get(c, "EntityName");
                          const isExpanded = expandedCompanyId === id;
                          return (
                            <React.Fragment key={id || i}>
                              <tr
                                onClick={() => setExpandedCompanyId(isExpanded ? null : id)}
                                style={{ cursor: "pointer" }}
                                className={isExpanded ? "table-active" : ""}
                              >
                                <td className="ps-3 fw-bold small">{name}</td>
                                <td className="extra-small text-muted">
                                  {GoldenSourceHelper.get(c, "TaxReference") || GoldenSourceHelper.get(c, "RegistrationNumber")}
                                </td>
                                <td className="text-end pe-3">
                                  <button className="btn btn-link btn-sm extra-small fw-bold p-0 text-decoration-none">
                                    {isExpanded ? "CLOSE" : "MANAGE SUPPLIERS"}
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan="3" className="p-0 border-0 bg-white shadow-inner">
                                    <SupplierController companyId={id} companyName={name} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr><td colSpan="3" className="text-center p-5 text-muted small italic">No corporate records found in registry.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 4d. TAXONOMY (per-company XBRL taxonomy binding) */}
            {activeTab === "TAXONOMY" && (
              <div className="card shadow-sm border-0 bg-white overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="extra-small text-muted py-3 ps-3">COMPANY</th>
                        <th className="extra-small text-muted py-3">REG ID</th>
                        <th className="extra-small text-muted py-3 text-end pe-3">MANAGE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyList.length > 0 ? (
                        companyList.map((c, i) => {
                          const id = GoldenSourceHelper.get(c, "CorporateEntityId");
                          const name = GoldenSourceHelper.get(c, "EntityName");
                          const isExpanded = expandedCompanyId === id;
                          return (
                            <React.Fragment key={id || i}>
                              <tr
                                onClick={() => setExpandedCompanyId(isExpanded ? null : id)}
                                style={{ cursor: "pointer" }}
                                className={isExpanded ? "table-active" : ""}
                              >
                                <td className="ps-3 fw-bold small">{name}</td>
                                <td className="extra-small text-muted">
                                  {GoldenSourceHelper.get(c, "TaxReference") || GoldenSourceHelper.get(c, "RegistrationNumber")}
                                </td>
                                <td className="text-end pe-3">
                                  <button className="btn btn-link btn-sm extra-small fw-bold p-0 text-decoration-none">
                                    {isExpanded ? "CLOSE" : "MANAGE TAXONOMY"}
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan="3" className="p-0 border-0 bg-white shadow-inner">
                                    <CompanyTaxonomyPanel companyId={id} companyName={name} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr><td colSpan="3" className="text-center p-5 text-muted small italic">No corporate records found in registry.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 5. FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/dashboard")}>
                <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
              </button>
              <div className="text-end">
                <span className="text-muted extra-small d-block">
                  {activeCompanyName ? "Working With" : "Workflow Context"}
                </span>
                <span className="fw-bold small">
                  {activeCompanyName || activeWorkflow?.name || "Corporate Management"}
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}